import { useCallback, useEffect, useState } from 'react';
import api from '../services/api';

/**
 * The GNews API key that backs News Beats. Admin-only, install-wide.
 *
 * One key for everyone because GNews bills per key, not per user — a per-user
 * setting would be asking each person to go buy their own subscription.
 *
 * The key is never sent back to the browser: the API returns a mask plus
 * `is_set`. The input therefore starts EMPTY rather than pre-filled, and blank
 * means "keep what's stored". That is deliberate — pre-filling with the mask is
 * how a save on an unrelated field ends up writing bullet characters over a
 * working credential. (The API refuses a masked write too, so this is belt and
 * braces, but the empty input is what stops anyone hitting that error.)
 */

const KEY = 'gnews_api_key';

export default function NewsApiSettings() {
  const [isSet, setIsSet] = useState(false);
  const [masked, setMasked] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    try {
      const setting = await api.getAdminSetting(KEY);
      setIsSet(setting.is_set);
      setMasked(setting.value);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    const value = draft.trim();
    if (!value) return;
    setSaving(true);
    setError(null);
    try {
      const setting = await api.updateAdminSetting(KEY, value);
      setIsSet(setting.is_set);
      setMasked(setting.value);
      setDraft('');
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    if (!window.confirm('Remove the GNews key? Every beat will stop fetching.')) {
      return;
    }
    setSaving(true);
    try {
      const setting = await api.updateAdminSetting(KEY, null);
      setIsSet(setting.is_set);
      setMasked(null);
      setDraft('');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-sm text-gray-500 dark:text-gray-400">Loading…</div>;
  }

  return (
    <div className="max-w-2xl space-y-4">
      <div>
        <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">
          News API
        </h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          News Beats fetch articles from{' '}
          <a
            href="https://gnews.io"
            target="_blank"
            rel="noreferrer"
            className="text-indigo-600 dark:text-indigo-400 hover:underline"
          >
            GNews
          </a>
          . One key serves every user on this install.
        </p>
      </div>

      {error && (
        <div className="rounded-md border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-700 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Status:
          </span>
          {isSet ? (
            <span className="text-sm text-emerald-700 dark:text-emerald-400">
              Configured {masked && <code className="text-xs">{masked}</code>}
            </span>
          ) : (
            <span className="text-sm text-amber-700 dark:text-amber-400">
              Not configured - beats cannot fetch articles
            </span>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {isSet ? 'Replace the key' : 'API key'}
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              autoComplete="off"
              spellCheck={false}
              style={{ WebkitTextSecurity: 'disc' } as React.CSSProperties}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={isSet ? 'Leave blank to keep the stored key' : '••••••••'}
              className="flex-1 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md px-3 py-2 text-sm font-mono"
            />
            <button
              onClick={save}
              disabled={saving || !draft.trim()}
              className="px-3 py-2 text-sm rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Stored encrypted. It is never shown again after saving - rotate it in
            GNews if you lose it.
          </p>
          {saved && (
            <p className="mt-1 text-xs text-emerald-600 dark:text-emerald-400">
              Saved.
            </p>
          )}
        </div>

        {isSet && (
          <button
            onClick={clear}
            disabled={saving}
            className="text-xs text-red-600 dark:text-red-400 hover:underline disabled:opacity-50"
          >
            Remove the key
          </button>
        )}
      </div>
    </div>
  );
}
